import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { analysisAPI, monitorAPI, teamAPI, notesAPI, foldersAPI, articlesAPI, icpAPI, billingAPI } from "../../lib/api";

const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

/* ── Time Ago Helper ── */
const timeAgo = (dateStr) => {
  if (!dateStr) return "N/A";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

/* ── Copy Share Link Helper ── */
const copyAnalysisLink = async (aid) => {
  if (!aid) { toast.error("Cannot copy link — analysis id missing"); return; }
  const url = `${window.location.origin}/app/analysis/${aid}`;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(url);
    } else {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("Link copied to clipboard", { duration: 3000 });
  } catch (e) {
    toast.error("Could not copy to clipboard");
  }
};

/* ── Fit Dot ── */
const FitDot = ({ score, size = 8, stale = false }) => {
  const color = score >= 75 ? "#22C55E" : score >= 45 ? "#F59E0B" : "#EF4444";
  return (
    <span data-testid={stale ? "fit-dot-stale" : "fit-dot"}
      title={stale ? "Scored against older ICP — re-analyze to update" : undefined}
      style={{
        position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: size, height: size, flexShrink: 0,
      }}>
      <span style={{
        width: size, height: size, borderRadius: "50%",
        background: color, boxShadow: `0 0 ${size}px ${color}66`, display: "block",
      }} />
      {stale && (
        <span style={{
          position: "absolute", right: -3, bottom: -3,
          width: Math.max(7, Math.round(size * 0.7)),
          height: Math.max(7, Math.round(size * 0.7)),
          borderRadius: "50%", background: "#0B0F19",
          border: "1px solid #F59E0B",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 0 4px rgba(245,158,11,0.55)",
        }}>
          <svg width={Math.max(5, Math.round(size * 0.5))} height={Math.max(5, Math.round(size * 0.5))} viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth={3} strokeLinecap="round">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
        </span>
      )}
    </span>
  );
};

/* ── Confidence Badge ── */
const ConfBadge = ({ level }) => {
  const map = { High: { bg: "#052E16", text: "#22C55E", border: "#14532D" }, Medium: { bg: "#451A03", text: "#F59E0B", border: "#78350F" }, Low: { bg: "#450A0A", text: "#EF4444", border: "#7F1D1D" } };
  const s = map[level] || map.Low;
  return (
    <span style={{ fontSize: 9, fontFamily: "'DM Mono', monospace", fontWeight: 600, color: s.text, background: s.bg, border: `1px solid ${s.border}`, borderRadius: 4, padding: "1px 6px" }}>
      {level.toUpperCase()}
    </span>
  );
};

/* ── Card Shell ── */
const DashCard = ({ id, title, subtitle, expanded, onToggle, locked, children, badge, badgeColor = "#F59E0B", tierLabel }) => {
  const bodyRef = useRef(null);
  const [bodyHeight, setBodyHeight] = useState(0);

  useEffect(() => {
    if (bodyRef.current) {
      setBodyHeight(bodyRef.current.scrollHeight);
    }
  }, [children, expanded]);

  return (
    <div style={{
      background: SURFACE,
      border: `1px solid ${expanded ? "#2D3748" : BORDER}`,
      borderRadius: 12,
      overflow: "hidden",
      transition: "border-color 0.2s",
      opacity: locked ? 0.5 : 1,
    }}>
      {/* Header */}
      <button
        onClick={() => !locked && onToggle(id)}
        style={{
          width: "100%", background: "none", border: "none", cursor: locked ? "not-allowed" : "pointer",
          padding: "16px 18px",
          display: "flex", alignItems: "center", gap: 10,
          textAlign: "left",
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.01em" }}>
              {title}
            </span>
            {badge !== undefined && (
              <span style={{
                fontSize: 10, fontWeight: 700, fontFamily: "'DM Mono', monospace",
                color: badgeColor, background: `${badgeColor}18`,
                border: `1px solid ${badgeColor}33`,
                borderRadius: 100, padding: "1px 7px",
              }}>{badge}</span>
            )}
            {tierLabel && (
              <span style={{ fontSize: 9, fontFamily: "'DM Mono', monospace", color: TEXT_DIM, background: "#161B27", border: `1px solid ${BORDER}`, borderRadius: 3, padding: "1px 5px" }}>
                {tierLabel}
              </span>
            )}
            {locked && (
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2}><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
            )}
          </div>
          {subtitle && <p style={{ fontSize: 11, color: TEXT_DIM, marginTop: 2, fontFamily: "'DM Sans', sans-serif" }}>{subtitle}</p>}
        </div>
        <svg
          width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2.5}
          strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink: 0, transform: expanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 200ms ease" }}
        >
          <path d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Collapsible body with smooth height transition */}
      {!locked && (
        <div ref={bodyRef} style={{
          height: expanded ? bodyHeight : 0,
          overflow: "hidden",
          transition: "height 200ms ease",
          borderTop: expanded ? `1px solid ${BORDER}` : "none",
          padding: 0,
        }}>
          {children}
        </div>
      )}
    </div>
  );
};

/* ── Row ── fixed 56px height across every card */
const Row = ({ children, onClick, subtle, "data-testid": testId }) => (
  <div
    onClick={onClick}
    data-testid={testId}
    style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 18px",
      height: 56,
      boxSizing: "border-box",
      overflow: "hidden",
      borderBottom: `1px solid ${BORDER}`,
      cursor: onClick ? "pointer" : "default",
      background: "transparent",
      transition: "background 0.15s",
    }}
    onMouseEnter={e => { if (onClick) e.currentTarget.style.background = SURFACE_2; }}
    onMouseLeave={e => { if (onClick) e.currentTarget.style.background = "transparent"; }}
  >
    {children}
  </div>
);

/* ── SpacerRow ── invisible 56px filler; no content, no border, no bg, no hover */
const SpacerRow = () => (
  <div style={{ height: 56, boxSizing: "border-box" }} aria-hidden="true" />
);

/* ── Card footer (VIEW ALL →) ── identical across all cards */
const CardFooter = ({ testId, label, onClick }) => (
  <div style={{ padding: "10px 18px", display: "flex", justifyContent: "flex-end" }}>
    <span data-testid={testId} onClick={() => onClick && onClick()} style={{ fontSize: 10, fontWeight: 600, color: ACCENT, cursor: "pointer", fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em" }}>{label}</span>
  </div>
);

const ActionBtn = ({ label, color = ACCENT, onClick, "data-testid": testId }) => (
  <button onClick={onClick} data-testid={testId} style={{
    fontSize: 10, fontWeight: 600, fontFamily: "'DM Mono', monospace",
    color, background: `${color}14`, border: `1px solid ${color}33`,
    borderRadius: 5, padding: "3px 8px", cursor: "pointer",
    transition: "all 0.15s", whiteSpace: "nowrap",
  }}
    onMouseEnter={e => e.currentTarget.style.background = `${color}24`}
    onMouseLeave={e => e.currentTarget.style.background = `${color}14`}
  >{label}</button>
);

/* ── DATA ──
 * These were previously seeded with demo records (Sarah K. / Marcus L. /
 * Klarna AB / Shopify Inc. / etc.) so the dashboard would look populated
 * during early-access demos. They appeared as REAL user data in production
 * even for accounts that had never collaborated or saved a summary, which
 * was misleading and confusing — multiple users reported the dashboard
 * showing other people's activity.
 *
 * All four arrays are now empty by default. The card components below
 * already gracefully render an empty state when their `data` prop is empty,
 * so on a fresh account every dashboard card will look quiet (correct
 * behaviour). When the user actually completes analyses / saves summaries
 * / creates folders / collaborates with teammates, those real records will
 * populate via the standard data-fetch effects in InferenceOverview. */
const RECENT = [];

const MONITORED = [];

const ACTIVITY = [];

const NOTES = [];

const FOLDERS = [];

const SUMMARIES = [];

const sevColor = (s) => s === "material" ? "#EF4444" : s === "minor" ? "#F59E0B" : "transparent";
const actionTypeColor = (t) => t === "analysis" ? ACCENT : t === "note" ? "#8B5CF6" : "#22C55E";

/* ── CARDS ── */
const RecentAnalysesCard = ({ expanded, onToggle, data, onViewAnalysis, onReanalyze, reanalyzingId, onViewAll, onSaveAnalysis, savedIds }) => {
  const all = data || [];
  const rows = all.slice(0, 5);

  return (
    <DashCard id="recent" title="Recent Analyses" badge={all.length} expanded={expanded} onToggle={onToggle}>
      {/* minHeight 280 = 5 rows × 56px — keeps this card visually aligned with
          its CardRow sibling (Monitored Companies) across every content count:
          empty state, 1-4 rows, or 5 rows. Populated path already hits 280
          via SpacerRow padding; empty path now matches via the wrapper. */}
      <div style={{ minHeight: 280 }}>
      {all.length === 0 ? (
        <div data-testid="recent-empty-card" style={{ padding: "32px 0", textAlign: "center" }}>
          <p style={{ fontSize: 13, color: TEXT_PRIMARY, marginBottom: 4 }}>No analyses yet.</p>
          <p style={{ fontSize: 12, color: TEXT_MUTED }}>Click <strong style={{ color: TEXT_PRIMARY }}>Analyze</strong> to run your first.</p>
        </div>
      ) : (<>
      {rows.map((r, i) => (
        <Row key={r.id || i} data-testid={`recent-row-${i}`} onClick={() => onViewAnalysis && r.id && onViewAnalysis(r.id)}>
          <FitDot score={r.fit} stale={r.stale} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</div>
            <div style={{ display: "flex", gap: 6, marginTop: 2, alignItems: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{r.industry}</span>
              <span style={{ fontSize: 10, color: TEXT_DIM }}>·</span>
              <span style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{r.source}</span>
            </div>
          </div>
          <ConfBadge level={r.conf} />
          <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap" }}>{r.ts}</span>
          {expanded && (
            <div style={{ display: "flex", gap: 5 }}>
              <button
                data-testid={`reanalyze-row-btn-${i}`}
                onClick={(e) => { e.stopPropagation(); onReanalyze && r.id && onReanalyze(r.id); }}
                disabled={reanalyzingId === r.id}
                style={{
                  fontSize: 10, fontWeight: 600, padding: "4px 10px", borderRadius: 5, cursor: reanalyzingId === r.id ? "wait" : "pointer",
                  border: `1px solid ${reanalyzingId === r.id ? ACCENT : BORDER}`,
                  background: reanalyzingId === r.id ? `${ACCENT}18` : "transparent",
                  color: reanalyzingId === r.id ? ACCENT : TEXT_MUTED,
                  fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em", transition: "all 0.15s",
                }}
              >
                {reanalyzingId === r.id ? "RE-ANALYZING..." : "RE-ANALYZE"}
              </button>
              <ActionBtn label="OPEN" data-testid={`open-row-btn-${i}`} onClick={(e) => { e.stopPropagation(); onViewAnalysis && r.id && onViewAnalysis(r.id); }} />
              <ActionBtn
                label="COPY LINK"
                data-testid={`copy-link-row-btn-${i}`}
                onClick={(e) => { e.stopPropagation(); copyAnalysisLink(r.id); }}
              />
              <ActionBtn
                label={(savedIds && savedIds.has(r.id)) || r.saved ? "SAVED" : "SAVE"}
                color={(savedIds && savedIds.has(r.id)) || r.saved ? "#22C55E" : TEXT_MUTED}
                onClick={(e) => { e.stopPropagation(); onSaveAnalysis && r.id && onSaveAnalysis(r.id); }}
              />
            </div>
          )}
        </Row>
      ))}
      {Array.from({ length: Math.max(0, 5 - rows.length) }).map((_, i) => <SpacerRow key={`sp-${i}`} />)}
      </>)}
      </div>
      {expanded && <CardFooter testId="view-all-analyses-btn" label="VIEW ALL ANALYSES →" onClick={onViewAll} />}
    </DashCard>
  );
};

const MonitoredCard = ({ expanded, onToggle, data, onViewAll, onOpenCompany }) => {
  const all = data || [];
  const monitors = all.slice(0, 5);
  return (
    <DashCard id="monitor" title="Monitored Companies" badge={all.filter(m => m.changed).length} badgeColor="#F59E0B" expanded={expanded} onToggle={onToggle}>
      {/* minHeight 280 = 5 rows × 56px — mirrors RecentAnalysesCard so this
          card always renders at the same body height as its row sibling. */}
      <div style={{ minHeight: 280 }}>
      {all.length === 0 ? (
        <div data-testid="monitored-empty-card" style={{ padding: "32px 0", textAlign: "center" }}>
          <p style={{ fontSize: 13, color: TEXT_PRIMARY, marginBottom: 4 }}>No monitored companies yet.</p>
          <p style={{ fontSize: 12, color: TEXT_MUTED }}>Open any analysis and click <strong style={{ color: TEXT_PRIMARY }}>Monitor</strong> to start tracking a company.</p>
        </div>
      ) : (<>
      {monitors.map((m, i) => (
      <Row key={i} data-testid={`monitor-row-${i}`} onClick={() => onOpenCompany && onOpenCompany(m.name || m.domain, "monitor")}>
        <div style={{ width: 7, height: 7, borderRadius: "50%", background: m.status === "Active" ? "#22C55E" : TEXT_DIM, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.name}</div>
          <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.industry} · {m.status} · {m.lastScan}</div>
        </div>
        {m.changed && (
          <span style={{
            fontSize: 9, fontWeight: 700, fontFamily: "'DM Mono', monospace",
            color: sevColor(m.severity), background: `${sevColor(m.severity)}14`,
            border: `1px solid ${sevColor(m.severity)}33`,
            borderRadius: 4, padding: "2px 6px", whiteSpace: "nowrap",
          }}>
            {m.severity?.toUpperCase()} CHANGE
          </span>
        )}
        {expanded && (
          <div style={{ display: "flex", gap: 5 }}>
            {m.changed && <ActionBtn label="VIEW DELTA" color="#F59E0B" data-testid={`monitor-delta-btn-${i}`} onClick={(e) => { e.stopPropagation(); onViewAll && onViewAll(); }} />}
            <ActionBtn label="OPEN" data-testid={`monitor-open-btn-${i}`} onClick={(e) => { e.stopPropagation(); onOpenCompany && onOpenCompany(m.name || m.domain, "monitor"); }} />
            <ActionBtn label={m.status === "Active" ? "PAUSE" : "RESUME"} color={TEXT_MUTED} data-testid={`monitor-pause-btn-${i}`} onClick={(e) => { e.stopPropagation(); onViewAll && onViewAll(); }} />
          </div>
        )}
      </Row>
    ))}
      {Array.from({ length: Math.max(0, 5 - monitors.length) }).map((_, i) => <SpacerRow key={`sp-${i}`} />)}
      </>)}
      </div>
      {expanded && <CardFooter testId="view-all-monitored-btn" label="VIEW ALL MONITORED →" onClick={onViewAll} />}
    </DashCard>
  );
};

const TeamActivityCard = ({ expanded, onToggle, data, onViewAll, onOpenCompany }) => {
  const activities = ((data && data.length > 0 ? data : ACTIVITY)).slice(0, 5);
  return (
    <DashCard id="activity" title="Team Activity" tierLabel="TEAM · ORG" badge={activities.length} expanded={expanded} onToggle={onToggle}>
      {activities.map((a, i) => (
        <Row key={i} data-testid={`activity-row-${i}`} onClick={() => onOpenCompany && onOpenCompany(a.company, "activity")}>
          <div style={{
            width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
            background: `${actionTypeColor(a.type)}22`,
            border: `1px solid ${actionTypeColor(a.type)}44`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 10, fontWeight: 700, color: actionTypeColor(a.type), fontFamily: "'DM Sans', sans-serif",
          }}>
            {a.user.split(" ").map(n => n[0]).join("")}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              <strong>{a.user}</strong> {a.action} — <span style={{ color: TEXT_MUTED }}>{a.company}</span>
            </div>
            <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.ts}</div>
          </div>
          {expanded && <ActionBtn label="OPEN RESULTS" data-testid={`activity-open-btn-${i}`} onClick={(e) => { e.stopPropagation(); onOpenCompany && onOpenCompany(a.company, "activity"); }} />}
        </Row>
      ))}
      {Array.from({ length: Math.max(0, 5 - activities.length) }).map((_, i) => <SpacerRow key={`sp-${i}`} />)}
      {expanded && <CardFooter testId="view-all-activity-btn" label="VIEW ALL ACTIVITY →" onClick={onViewAll} />}
    </DashCard>
  );
};

const DecisionNotesCard = ({ expanded, onToggle, data, onViewAll, onOpenCompany }) => {
  const notes = ((data && data.length > 0 ? data : NOTES)).slice(0, 5);
  return (
    <DashCard id="notes" title="Decision Notes" tierLabel="TEAM · ORG" badge={notes.length} expanded={expanded} onToggle={onToggle}>
      {notes.map((n, i) => (
        <Row key={i} data-testid={`notes-row-${i}`} onClick={() => onOpenCompany && onOpenCompany(n.company, "notes")}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap", overflow: "hidden" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", overflow: "hidden", textOverflow: "ellipsis", flex: 1, minWidth: 0 }}>{n.title}</span>
              <span style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>{n.company} · {n.author} · {n.ts}</span>
            </div>
            <div style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {n.snippet}
            </div>
          </div>
          {expanded && <ActionBtn label="OPEN" data-testid={`notes-open-btn-${i}`} onClick={(e) => { e.stopPropagation(); onOpenCompany && onOpenCompany(n.company, "notes"); }} />}
        </Row>
      ))}
      {Array.from({ length: Math.max(0, 5 - notes.length) }).map((_, i) => <SpacerRow key={`sp-${i}`} />)}
      {expanded && <CardFooter testId="view-all-notes-btn" label="VIEW ALL NOTES →" onClick={onViewAll} />}
    </DashCard>
  );
};

const SharedFoldersCard = ({ expanded, onToggle, data, onViewAll }) => {
  const folders = ((data && data.length > 0 ? data : FOLDERS)).slice(0, 5);
  const permColor = (p) => p === "Org" ? "#8B5CF6" : p === "Team" ? ACCENT : TEXT_DIM;
  return (
    <DashCard id="folders" title="Shared Folders" tierLabel="TEAM · ORG" badge={folders.length} expanded={expanded} onToggle={onToggle}>
      {folders.map((f, i) => (
        <Row key={i} data-testid={`folders-row-${i}`} onClick={() => onViewAll && onViewAll()}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth={1.8}><path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</div>
            <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.count} companies · Updated {f.updated}</div>
          </div>
          <span style={{
            fontSize: 9, fontWeight: 600, color: permColor(f.perm),
            background: `${permColor(f.perm)}14`, border: `1px solid ${permColor(f.perm)}33`,
            borderRadius: 4, padding: "2px 6px", fontFamily: "'DM Mono', monospace",
          }}>{f.perm.toUpperCase()}</span>
          {expanded && <ActionBtn label="OPEN" data-testid={`folders-open-btn-${i}`} onClick={(e) => { e.stopPropagation(); onViewAll && onViewAll(); }} />}
        </Row>
      ))}
      {Array.from({ length: Math.max(0, 5 - folders.length) }).map((_, i) => <SpacerRow key={`sp-${i}`} />)}
      {expanded && <CardFooter testId="view-all-folders-btn" label="VIEW ALL FOLDERS →" onClick={onViewAll} />}
    </DashCard>
  );
};

const SavedSummariesCard = ({ expanded, onToggle, data, onViewAll, onOpenCompany }) => {
  const summaries = ((data && data.length > 0 ? data : SUMMARIES)).slice(0, 5);
  return (
    <DashCard id="summaries" title="Saved Summaries" tierLabel="TEAM · ORG" badge={summaries.length} expanded={expanded} onToggle={onToggle}>
      {summaries.map((s, i) => (
        <Row key={i} data-testid={`summaries-row-${i}`} onClick={() => onOpenCompany && onOpenCompany(s.company, "savedSummaries")}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth={1.8}><path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</div>
            <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.company} · {s.folder} · {s.ts}</div>
          </div>
          {expanded && <ActionBtn label="OPEN" data-testid={`summaries-open-btn-${i}`} onClick={(e) => { e.stopPropagation(); onOpenCompany && onOpenCompany(s.company, "savedSummaries"); }} />}
        </Row>
      ))}
      {Array.from({ length: Math.max(0, 5 - summaries.length) }).map((_, i) => <SpacerRow key={`sp-${i}`} />)}
      {expanded && <CardFooter testId="view-all-summaries-btn" label="VIEW ALL SUMMARIES →" onClick={onViewAll} />}
    </DashCard>
  );
};

/* ── Sidebar + TopBar (mini, embedded) ── */
const MiniSidebar = ({ active, onNav }) => {
  const items = [
    { id: "overview", icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6", label: "Overview" },
    { id: "analyze", icon: "M12 4v16m8-8H4", label: "Analyze", isAction: true },
    null,
    { id: "articles", icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10", label: "Articles" },
    { id: "monitor", icon: "M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z", label: "Monitor", badge: 3 },
    { id: "recent", icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z", label: "Recent" },
    null,
    { id: "activity", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2", label: "Activity", locked: true },
    { id: "notes", icon: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z", label: "Notes", locked: true },
    { id: "folders", icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z", label: "Folders", locked: true },
    { id: "summaries", icon: "M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z", label: "Summaries", locked: true },
  ];

  return (
    <aside style={{
      width: 52, background: SURFACE, borderRight: `1px solid ${BORDER}`,
      display: "flex", flexDirection: "column", height: "100%", flexShrink: 0,
    }}>
      <div style={{ height: 52, borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          <span style={{ fontWeight: 800, fontSize: 11, color: TEXT_PRIMARY, letterSpacing: "-0.02em" }}>IN</span>
          <span style={{ width: 3, height: 3, borderRadius: "50%", background: ACCENT, marginBottom: 4 }} />
        </div>
      </div>
      <nav style={{ flex: 1, padding: "8px 6px", display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
        {items.map((item, i) => {
          if (!item) return <div key={i} style={{ height: 1, background: BORDER, margin: "4px 0" }} />;
          const isActive = active === item.id;
          return (
            <button key={item.id} onClick={() => !item.locked && onNav(item.id)} title={item.label} style={{
              width: 38, height: 34, borderRadius: 7, border: "none",
              background: isActive ? `${ACCENT}20` : "transparent",
              color: item.locked ? TEXT_DIM : isActive ? ACCENT : TEXT_MUTED,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: item.locked ? "not-allowed" : "pointer", opacity: item.locked ? 0.4 : 1,
              position: "relative", transition: "all 0.15s",
            }}
              onMouseEnter={e => { if (!item.locked && !isActive) e.currentTarget.style.background = "#161B27"; }}
              onMouseLeave={e => { if (!item.locked && !isActive) e.currentTarget.style.background = "transparent"; }}
            >
              {isActive && <div style={{ position: "absolute", left: -1, top: "20%", bottom: "20%", width: 2, borderRadius: "0 2px 2px 0", background: ACCENT }} />}
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={isActive ? 2 : 1.6} strokeLinecap="round" strokeLinejoin="round">
                <path d={item.icon} />
                {item.id === "monitor" && <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />}
              </svg>
              {item.badge && <div style={{ position: "absolute", top: 4, right: 4, width: 5, height: 5, borderRadius: "50%", background: "#F59E0B" }} />}
            </button>
          );
        })}
      </nav>
      <div style={{ padding: "10px 7px", borderTop: `1px solid ${BORDER}` }}>
        <div style={{
          width: 30, height: 30, borderRadius: "50%", margin: "0 auto",
          background: `linear-gradient(135deg, ${ACCENT} 0%, #1D4ED8 100%)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 11, fontWeight: 700, color: "#fff",
        }}>AJ</div>
      </div>
    </aside>
  );
};

/* ── ICP Stale Warning Card (Dashboard) ── */
const ICPStaleCard = ({ summary, onRescore, onDismiss, isRescoring, rescoreResult }) => {
  const stale = summary?.stale_count || 0;
  const canRescore = summary?.can_rescore_count || 0;
  const capRemaining = summary?.cap_remaining || 0;
  const overflow = stale > canRescore ? stale - canRescore : 0;
  return (
    <div data-testid="icp-stale-card" style={{
      background: "linear-gradient(135deg, #2A1B05 0%, #1A1108 100%)",
      border: `1px solid #78350F`,
      borderRadius: 12,
      padding: "18px 20px",
      marginBottom: 22,
      display: "flex", alignItems: "center", gap: 16,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: "#F59E0B22", border: `1px solid #78350F`,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 4 23 10 17 10" />
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#FCD34D", marginBottom: 3, letterSpacing: "-0.01em" }}>
          Your ICP profile has changed.
        </div>
        <div style={{ fontSize: 12, color: "#B45309", lineHeight: 1.5 }}>
          {rescoreResult ? (
            <>
              {rescoreResult.queued > 0 && <>{rescoreResult.queued} analyses queued for re-analysis.</>}
              {rescoreResult.skipped_due_to_cap > 0 && <> {rescoreResult.skipped_due_to_cap} skipped — monthly cap reached.</>}
            </>
          ) : (
            <>
              Some of your analyses may show outdated fit scores and signals. Re-analyze to get scores based on your current criteria.
              {" "}<strong style={{ color: "#FCD34D" }}>{stale} {stale === 1 ? "analysis" : "analyses"}</strong> may need rescoring
              {overflow > 0 && (
                <span style={{ color: "#F87171" }}>
                  {" "}— monthly cap allows only <strong>{canRescore}</strong> right now ({capRemaining} remaining)
                </span>
              )}.
            </>
          )}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <button data-testid="icp-stale-cta" onClick={onRescore} disabled={isRescoring || canRescore === 0} style={{
          background: (isRescoring || canRescore === 0) ? "#78350F" : "#F59E0B",
          color: "#111", border: "none",
          borderRadius: 8, padding: "9px 16px", fontSize: 12, fontWeight: 700,
          cursor: (isRescoring || canRescore === 0) ? "not-allowed" : "pointer",
          fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap",
          transition: "background 0.15s",
          opacity: (isRescoring || canRescore === 0) ? 0.7 : 1,
        }}
          onMouseEnter={e => { if (!isRescoring && canRescore > 0) e.currentTarget.style.background = "#FBBF24"; }}
          onMouseLeave={e => { if (!isRescoring && canRescore > 0) e.currentTarget.style.background = "#F59E0B"; }}
        >{isRescoring ? "Re-analyzing..." : `Re-analyze${canRescore < stale && canRescore > 0 ? ` ${canRescore}` : " All"}`}</button>
        <button data-testid="icp-stale-dismiss" onClick={onDismiss} aria-label="Dismiss" style={{
          background: "transparent", color: "#B45309", border: "none",
          cursor: "pointer", padding: 6, display: "flex", alignItems: "center",
          borderRadius: 6, transition: "color 0.15s",
        }}
          onMouseEnter={e => { e.currentTarget.style.color = "#FCD34D"; }}
          onMouseLeave={e => { e.currentTarget.style.color = "#B45309"; }}
        >
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

/* ── ICP Incomplete Warning Card (Dashboard) ── */
const ICPIncompleteCard = ({ onGoToSettings, onDismiss }) => (
  <div data-testid="icp-incomplete-card" style={{
    background: "linear-gradient(135deg, #2A1B05 0%, #1A1108 100%)",
    border: `1px solid #78350F`,
    borderRadius: 12,
    padding: "18px 20px",
    marginBottom: 22,
    display: "flex", alignItems: "center", gap: 16,
    fontFamily: "'DM Sans', sans-serif",
  }}>
    <div style={{
      width: 40, height: 40, borderRadius: 10,
      background: "#F59E0B22", border: `1px solid #78350F`,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
    }}>
      <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#FCD34D", marginBottom: 3, letterSpacing: "-0.01em" }}>
        Your ICP profile is incomplete
      </div>
      <div style={{ fontSize: 12, color: "#B45309", lineHeight: 1.5 }}>
        Add target industries and personas to sharpen Fit scoring, unlock ICP Match insights, and personalize every Brief. Takes under 2 minutes.
      </div>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
      <button data-testid="icp-card-cta" onClick={onGoToSettings} style={{
        background: "#F59E0B", color: "#111", border: "none",
        borderRadius: 8, padding: "9px 16px", fontSize: 12, fontWeight: 700,
        cursor: "pointer", fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap",
        transition: "background 0.15s",
      }}
        onMouseEnter={e => { e.currentTarget.style.background = "#FBBF24"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#F59E0B"; }}
      >Complete ICP</button>
      <button data-testid="icp-card-dismiss" onClick={onDismiss} aria-label="Dismiss" style={{
        background: "transparent", color: "#B45309", border: "none",
        cursor: "pointer", padding: 6, display: "flex", alignItems: "center",
        borderRadius: 6, transition: "color 0.15s",
      }}
        onMouseEnter={e => { e.currentTarget.style.color = "#FCD34D"; }}
        onMouseLeave={e => { e.currentTarget.style.color = "#B45309"; }}
      >
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  </div>
);

/* ── ROOT ── */
export default function InferenceOverview({ onViewAnalysis, workspaceId, userName, onNavigate, savedIds, onSaveAnalysis }) {
  // Each card is independently togglable with its own localStorage key (matches company results page pattern).
  const CARD_IDS = ["recent", "monitor", "activity", "notes", "folders", "summaries"];
  const storageKeyFor = (id) => `inference.dashboard.card.${id}`;
  const [expanded, setExpanded] = useState(() => {
    const init = {};
    for (const id of CARD_IDS) {
      try {
        const v = localStorage.getItem(storageKeyFor(id));
        init[id] = v === null ? (id === "recent") : v === "1";
      } catch {
        init[id] = id === "recent";
      }
    }
    return init;
  });
  const [recentData, setRecentData] = useState([]);
  const [monitorData, setMonitorData] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [notesData, setNotesData] = useState([]);
  const [foldersData, setFoldersData] = useState([]);
  const [summariesData, setSummariesData] = useState([]);
  const [reanalyzingId, setReanalyzingId] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null); // { total, completed, running }
  const [stats, setStats] = useState({ analyses: 0, monitored: 0, changes: 0, outputs: 0 });
  const [icpConfig, setIcpConfig] = useState(null);
  const [icpLoaded, setIcpLoaded] = useState(false);
  const [icpCardDismissed, setIcpCardDismissed] = useState(() => {
    try { return sessionStorage.getItem("inference_icp_card_dismissed") === "1"; } catch { return false; }
  });

  // Workspace tier — used to gate the "Unlock Team & Org" upsell CTA so it
  // only shows for users below the Team plan. Tier ladder: free < tier_2 <
  // tier_2_5 < tier_3 < tier_4 < tier_4_plus. We map the tier string to a
  // numeric rank and hide the upsell at rank >= 3.
  const [tierRank, setTierRank] = useState(null);
  useEffect(() => {
    const RANK = { free: 0, tier_1: 1, tier_2: 2, tier_2_5: 2, tier_3: 3, tier_4: 4, tier_4_plus: 5 };
    billingAPI.workspaceInfo()
      .then(r => {
        const t = r?.data?.tier_id || r?.data?.tier || "free";
        setTierRank(RANK[t] ?? 0);
      })
      .catch(() => setTierRank(0));
  }, []);
  const showUpsell = tierRank !== null && tierRank < 3;

  // ── ICP Stale dashboard nudge state ──
  const [staleSummary, setStaleSummary] = useState(null); // {stale_count, cap_remaining, can_rescore_count}
  const [staleDismissed, setStaleDismissed] = useState(() => {
    try { return sessionStorage.getItem("inference_icp_stale_dismissed") === "1"; } catch { return false; }
  });
  const [staleRescoring, setStaleRescoring] = useState(false);
  const [staleRescoreResult, setStaleRescoreResult] = useState(null);

  const fetchStaleSummary = async () => {
    if (!workspaceId) return;
    try {
      const res = await analysisAPI.staleSummary(workspaceId);
      setStaleSummary(res.data);
    } catch (err) { /* ignore */ }
  };

  useEffect(() => { fetchStaleSummary(); /* eslint-disable-next-line */ }, [workspaceId]);

  const handleStaleRescore = async () => {
    if (!workspaceId || staleRescoring) return;
    setStaleRescoring(true);
    try {
      const res = await analysisAPI.bulkReanalyzeStale(workspaceId);
      setStaleRescoreResult(res.data);
      // Refresh recent analyses + stale summary after a short delay so polling clears.
      setTimeout(async () => {
        try {
          const listRes = await analysisAPI.list(workspaceId);
          if (listRes.data?.length > 0) {
            setRecentData(listRes.data.map(a => ({
              id: a.analysis_id, name: a.company_name || "Unknown", fit: a.fit_score || 0,
              conf: a.fit_score >= 75 ? "High" : a.fit_score >= 45 ? "Medium" : "Low",
              ts: timeAgo(a.created_at), source: a.source || "Manual", saved: false,
              stale: !!a.icp_stale,
              industry: a.domain ? a.domain.replace(/\.\w+$/, "") : "N/A",
            })));
          }
          await fetchStaleSummary();
        } catch (err) { /* ignore */ }
        setStaleRescoring(false);
      }, 4000);
    } catch (err) {
      console.error("Stale rescore failed:", err);
      setStaleRescoring(false);
    }
  };

  const handleStaleDismiss = () => {
    try { sessionStorage.setItem("inference_icp_stale_dismissed", "1"); } catch (e) { /* ignore */ }
    setStaleDismissed(true);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await icpAPI.get();
        if (res.data) setIcpConfig(res.data);
      } catch (err) { /* no ICP configured */ }
      setIcpLoaded(true);
    })();
  }, []);

  const icpIncomplete = icpLoaded && (
    !icpConfig ||
    ((!icpConfig.target_industries || icpConfig.target_industries.length === 0) &&
     (!icpConfig.target_personas || icpConfig.target_personas.length === 0))
  );

  const handleIcpCardCTA = () => {
    try { sessionStorage.setItem("inference_settings_initial_section", "icp"); } catch (e) { /* ignore */ }
    if (onNavigate) onNavigate("settings");
  };
  const handleIcpCardDismiss = () => {
    try { sessionStorage.setItem("inference_icp_card_dismissed", "1"); } catch (e) { /* ignore */ }
    setIcpCardDismissed(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      let dashboardErrorShown = false;
      const showDashboardError = () => {
        if (!dashboardErrorShown) { toast.error("Could not load dashboard data."); dashboardErrorShown = true; }
      };
      if (workspaceId) {
        try {
          const res = await analysisAPI.list(workspaceId);
          if (res.data && res.data.length > 0) {
            const mapped = res.data.map(a => ({
              id: a.analysis_id,
              name: a.company_name || "Unknown",
              fit: a.fit_score || 0,
              conf: a.fit_score >= 75 ? "High" : a.fit_score >= 45 ? "Medium" : "Low",
              ts: timeAgo(a.created_at),
              source: a.source || "Manual",
              saved: false,
              stale: !!a.icp_stale,
              industry: a.domain ? a.domain.replace(/\.\w+$/, "") : "N/A",
              domain: a.domain || "",
            }));
            setRecentData(mapped);
            setStats(s => ({ ...s, analyses: mapped.length, outputs: mapped.length * 3 }));
          }
        } catch (err) { console.error("Failed to fetch analyses:", err); showDashboardError(); }
      }
      try {
        const monRes = await monitorAPI.list();
        if (monRes.data && monRes.data.length > 0) {
          const mapped = monRes.data.map(m => ({
            name: m.company_name || "Unknown",
            domain: m.domain || "",
            status: m.status === "active" ? "Active" : "Paused",
            lastScan: m.last_scan ? timeAgo(m.last_scan) : "Never",
            changed: m.changed || false,
            severity: m.severity || null,
            industry: m.domain ? m.domain.replace(/\.\w+$/, "") : "N/A",
          }));
          setMonitorData(mapped);
          setStats(s => ({ ...s, monitored: mapped.filter(m => m.status === "Active").length, changes: mapped.filter(m => m.changed).length }));
        }
      } catch (err) { console.error("Failed to fetch monitors:", err); showDashboardError(); }

      // Fetch team activity
      try {
        const teamsRes = await teamAPI.list();
        if (teamsRes.data && teamsRes.data.length > 0) {
          const teamId = teamsRes.data[0].team_id;
          const actRes = await teamAPI.activity(teamId);
          if (actRes.data && actRes.data.length > 0) {
            const mapped = actRes.data.map(a => ({
              user: a.user_name || "Unknown",
              action: a.action || "",
              company: a.target || "",
              type: a.type || "analysis",
              ts: timeAgo(a.created_at),
            }));
            setActivityData(mapped);
          }
        }
      } catch (err) { console.error("Failed to fetch team activity:", err); showDashboardError(); }

      // Fetch notes
      try {
        const notesRes = await notesAPI.list();
        if (notesRes.data && notesRes.data.length > 0) {
          const mapped = notesRes.data.map(n => ({
            company: n.company || "N/A",
            title: n.title || "Untitled",
            snippet: n.content || "",
            author: n.author_name || "Unknown",
            ts: timeAgo(n.created_at),
          }));
          setNotesData(mapped);
        }
      } catch (err) { console.error("Failed to fetch notes:", err); showDashboardError(); }

      // Fetch folders
      try {
        const foldersRes = await foldersAPI.list();
        if (foldersRes.data && foldersRes.data.length > 0) {
          const mapped = foldersRes.data.map(f => ({
            name: f.name || "Untitled",
            count: f.companies || 0,
            updated: timeAgo(f.updated_at),
            perm: f.permission || "Private",
          }));
          setFoldersData(mapped);
        }
      } catch (err) { console.error("Failed to fetch folders:", err); showDashboardError(); }

      // Fetch AI article summaries
      try {
        const sumRes = await articlesAPI.summaries();
        if (sumRes.data && sumRes.data.length > 0) {
          const mapped = sumRes.data.map(s => ({
            title: s.article_title || "Untitled article",
            company: s.company_name || "N/A",
            folder: "AI summary",
            ts: timeAgo(s.created_at),
          }));
          setSummariesData(mapped);
        }
      } catch (err) { console.error("Failed to fetch saved summaries:", err); showDashboardError(); }
    };
    fetchData();
  }, [workspaceId]);

  // Resolve a company name/domain mentioned in a dashboard row to its brief.
  // Falls back to the matching section when no analysis exists for it yet.
  const openCompany = (nameOrDomain, fallbackSection) => {
    const q = String(nameOrDomain || "").trim().toLowerCase();
    const hit = q && recentData.find(r => {
      const n = String(r.name || "").toLowerCase();
      const d = String(r.domain || "").toLowerCase();
      return (n && (n === q || n.includes(q) || q.includes(n))) || (d && (d === q || d.includes(q) || q.includes(d)));
    });
    if (hit && onViewAnalysis) onViewAnalysis(hit.id);
    else if (onNavigate && fallbackSection) onNavigate(fallbackSection);
  };

  const toggle = (id) => setExpanded(prev => {
    const next = { ...prev, [id]: !prev[id] };
    try { localStorage.setItem(storageKeyFor(id), next[id] ? "1" : "0"); } catch {}
    return next;
  });

  const handleReanalyze = async (analysisId) => {
    if (!workspaceId || !analysisId || reanalyzingId) return;
    setReanalyzingId(analysisId);
    try {
      await analysisAPI.reanalyze(workspaceId, analysisId);
      const pollInterval = setInterval(async () => {
        try {
          const pollRes = await analysisAPI.poll(workspaceId, analysisId);
          if (pollRes.data?.status === "completed" || pollRes.data?.status === "failed") {
            clearInterval(pollInterval);
            // Refresh the analyses list
            const res = await analysisAPI.list(workspaceId);
            if (res.data && res.data.length > 0) {
              const mapped = res.data.map(a => ({
                id: a.analysis_id, name: a.company_name || "Unknown", fit: a.fit_score || 0,
                conf: a.fit_score >= 75 ? "High" : a.fit_score >= 45 ? "Medium" : "Low",
                ts: timeAgo(a.created_at), source: a.source || "Manual", saved: false,
                stale: !!a.icp_stale,
                industry: a.domain ? a.domain.replace(/\.\w+$/, "") : "N/A",
              }));
              setRecentData(mapped);
            }
            setReanalyzingId(null);
          }
        } catch (err) {
          clearInterval(pollInterval);
          setReanalyzingId(null);
        }
      }, 3000);
    } catch (err) {
      console.error("Re-analyze failed:", err);
      setReanalyzingId(null);
    }
  };

  const handleBulkReanalyze = async () => {
    if (!workspaceId || bulkProgress) return;
    try {
      const res = await analysisAPI.bulkReanalyze(workspaceId);
      const ids = res.data?.analysis_ids || [];
      if (ids.length === 0) return;
      setBulkProgress({ total: ids.length, completed: 0, running: true });

      const pollBulk = setInterval(async () => {
        let done = 0;
        for (const aid of ids) {
          try {
            const p = await analysisAPI.poll(workspaceId, aid);
            if (p.data?.status === "completed" || p.data?.status === "failed") done++;
          } catch { done++; }
        }
        setBulkProgress(prev => ({ ...prev, completed: done }));
        if (done >= ids.length) {
          clearInterval(pollBulk);
          // Refresh list
          try {
            const listRes = await analysisAPI.list(workspaceId);
            if (listRes.data?.length > 0) {
              setRecentData(listRes.data.map(a => ({
                id: a.analysis_id, name: a.company_name || "Unknown", fit: a.fit_score || 0,
                conf: a.fit_score >= 75 ? "High" : a.fit_score >= 45 ? "Medium" : "Low",
                ts: timeAgo(a.created_at), source: a.source || "Manual", saved: false,
                stale: !!a.icp_stale,
                industry: a.domain ? a.domain.replace(/\.\w+$/, "") : "N/A",
              })));
              setStats(s => ({ ...s, analyses: listRes.data.length }));
            }
          } catch {}
          setBulkProgress(prev => ({ ...prev, running: false }));
          setTimeout(() => setBulkProgress(null), 4000);
        }
      }, 5000);
    } catch (err) {
      console.error("Bulk re-analyze failed:", err);
      setBulkProgress(null);
    }
  };

  return (
    <div data-testid="overview-page" style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #1E2433; border-radius: 4px; }
        button { outline: none; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      <div style={{ padding: "24px 28px" }}>
        {/* Page heading */}
        <div style={{ marginBottom: 22, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.02em", marginBottom: 4 }}>
              Good morning, {userName ? userName.split(" ")[0] : "there"}.
            </h1>
            <p style={{ fontSize: 13, color: TEXT_MUTED }}>{stats.changes > 0 ? `${stats.changes} monitored companies have material changes.` : ""} {stats.analyses} analyses completed.</p>
          </div>
          <button
            data-testid="bulk-reanalyze-btn"
            onClick={handleBulkReanalyze}
            disabled={!!bulkProgress?.running || recentData.length === 0}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              background: bulkProgress?.running ? `${ACCENT}18` : SURFACE,
              color: bulkProgress?.running ? ACCENT : TEXT_MUTED,
              border: `1px solid ${bulkProgress?.running ? ACCENT : BORDER}`,
              borderRadius: 8, padding: "10px 18px",
              fontSize: 12, fontWeight: 600, cursor: bulkProgress?.running ? "default" : "pointer",
              fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap",
              transition: "all 0.2s",
            }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} style={{ animation: bulkProgress?.running ? "spin 1s linear infinite" : "none" }}>
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {bulkProgress
              ? bulkProgress.running
                ? `Re-analyzing ${bulkProgress.completed}/${bulkProgress.total}...`
                : `Done! ${bulkProgress.total} updated`
              : `Re-analyze All (${recentData.length})`}
          </button>
        </div>

        {/* ICP Stale Warning Card (re-score nudge) */}
        {!staleDismissed && staleSummary && staleSummary.stale_count > 0 && (
          <ICPStaleCard
            summary={staleSummary}
            onRescore={handleStaleRescore}
            onDismiss={handleStaleDismiss}
            isRescoring={staleRescoring}
            rescoreResult={staleRescoreResult}
          />
        )}

        {/* ICP Incomplete Warning Card */}
        {icpIncomplete && !icpCardDismissed && (
          <ICPIncompleteCard onGoToSettings={handleIcpCardCTA} onDismiss={handleIcpCardDismiss} />
        )}

        {/* Stats strip */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 22 }}>
          {[
            { label: "Analyses this week", value: String(stats.analyses), delta: null, up: null },
            { label: "Monitored active", value: String(stats.monitored), delta: null, up: null },
            { label: "Decision Notes", value: String(notesData.length || 0), delta: null, up: null },
            { label: "Shared Folders", value: String(foldersData.length || 0), delta: null, up: null },
          ].map((s, i) => (
            <div key={i} style={{
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderRadius: 10, padding: "14px 16px",
            }}>
              <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 6, letterSpacing: "0.04em" }}>{s.label.toUpperCase()}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.02em" }}>{s.value}</span>
                {s.delta && (
                  <span style={{ fontSize: 11, color: s.up ? "#22C55E" : "#F59E0B", fontFamily: "'DM Mono', monospace" }}>
                    {s.delta}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Cards grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "start" }}>
          <RecentAnalysesCard expanded={!!expanded.recent} onToggle={toggle} data={recentData} onViewAnalysis={onViewAnalysis} onReanalyze={handleReanalyze} reanalyzingId={reanalyzingId} onViewAll={() => onNavigate && onNavigate("recent")} onSaveAnalysis={onSaveAnalysis} savedIds={savedIds} />
          <MonitoredCard expanded={!!expanded.monitor} onToggle={toggle} data={monitorData} onViewAll={() => onNavigate && onNavigate("monitor")} onOpenCompany={openCompany} />
          <TeamActivityCard expanded={!!expanded.activity} onToggle={toggle} data={activityData} onViewAll={() => onNavigate && onNavigate("activity")} onOpenCompany={openCompany} />
          <DecisionNotesCard expanded={!!expanded.notes} onToggle={toggle} data={notesData} onViewAll={() => onNavigate && onNavigate("notes")} onOpenCompany={openCompany} />
          <SharedFoldersCard expanded={!!expanded.folders} onToggle={toggle} data={foldersData} onViewAll={() => onNavigate && onNavigate("folders")} />
          <SavedSummariesCard expanded={!!expanded.summaries} onToggle={toggle} data={summariesData} onViewAll={() => onNavigate && onNavigate("savedSummaries")} onOpenCompany={openCompany} />
        </div>

        {/* Upgrade CTA — hidden for workspaces already on Team plan or above. */}
        {showUpsell && (
        <div data-testid="overview-upsell-team-org" style={{
          marginTop: 22,
          background: `linear-gradient(135deg, #0D1829 0%, #0A0D13 100%)`,
          border: `1px solid ${ACCENT}33`,
          borderRadius: 12,
          padding: "20px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 4 }}>
              Unlock Team & Org features
            </div>
            <div style={{ fontSize: 12, color: TEXT_MUTED }}>
              Decision Notes, Shared Folders, Team Activity, Monitoring Cadence, and Audit Trail.
            </div>
          </div>
          <button style={{
            background: ACCENT, color: "#fff", border: "none",
            borderRadius: 8, padding: "10px 20px",
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap",
            transition: "background 0.2s",
          }}
            onMouseEnter={e => e.currentTarget.style.background = "#1D4ED8"}
            onMouseLeave={e => e.currentTarget.style.background = ACCENT}
          >
            Upgrade Plan →
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
